import type {
  AudioFileIssueSeverity,
  AudioFileIssueType,
  OriginalBitrateInspectionStatus,
} from '@/composables/api/useAudioFileIssuesApi';

/**
 * Frontend mirrors of the PHP enums backing audio-file-issues filters.
 *
 * The TS types living in `useAudioFileIssuesApi.ts` are the authoritative
 * compile-time shape; this file ships the runtime option lists the filter
 * builder feeds into its dropdowns. Order matches the PHP `cases()` ordering
 * so the UI groups severities by their canonical priority and the issue-type
 * list reads in the same sequence the backend defines.
 *
 * If a case is added to the PHP enum without updating this file, the type
 * union still compiles, but the filter dropdown won't surface the new option.
 * The table keeps rendering such rows (the API serves the label), so nothing
 * visibly breaks — the case just cannot be filtered for, which is how
 * `normalization_metadata_missing` went missing for a while. Keep them in
 * lock-step: `audioFileIssueEnums.test.ts` pins the list to the PHP cases, so
 * a new case is added there as well (or fetch from
 * `/api/audio-file-issues/filter-schema` — for now a static mirror is good
 * enough and avoids an async dependency on a synchronous filter-builder render).
 */

export interface AudioFileIssueEnumOption<T extends string> {
    value: T;
    label: string;
}

export const AUDIO_FILE_ISSUE_SEVERITY_OPTIONS: AudioFileIssueEnumOption<AudioFileIssueSeverity>[] = [
    { value: 'error', label: 'Error' },
    { value: 'warning', label: 'Warning' },
    { value: 'info', label: 'Info' },
];

/**
 * The track quality tiers, in best-to-worst order. Only these three values are
 * stored, so the Rating filter offers them as a list to pick from rather than
 * a text box to type into. {@link TrackRatingBadge} colours them by first
 * letter, so a new tier would need a colour there too.
 */
export const TRACK_RATING_OPTIONS: AudioFileIssueEnumOption<string>[] = [
    { value: 'A1', label: 'A1' },
    { value: 'B1', label: 'B1' },
    { value: 'C1', label: 'C1' },
];

export const AUDIO_FILE_ISSUE_SEVERITY_LABELS: Record<AudioFileIssueSeverity, string> = Object.freeze({
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
});

/** Mirror of `App\Enums\AudioFileIssueType` — order matches the PHP cases() ordering. */
export const AUDIO_FILE_ISSUE_TYPE_OPTIONS: AudioFileIssueEnumOption<AudioFileIssueType>[] = [
    { value: 'gvlibrary_missing', label: 'gvlibrary file missing' },
    { value: 'gvlibrary_size_mismatch', label: 'gvlibrary encrypted size mismatch' },
    { value: 'gvlibrary_unreadable', label: 'gvlibrary unreadable' },
    { value: 'original_missing', label: 'Original-bitrate backup missing' },
    { value: 'original_unreadable', label: 'Original-bitrate unreadable' },
    { value: 'vbr_detected', label: 'Variable bitrate detected' },
    { value: 'bitrate_mismatch', label: 'DB bitrate is different from actual bitrate' },
    { value: 'low_bitrate', label: 'Below target bitrate' },
    { value: 'hls_master_missing', label: 'HLS master playlist missing' },
    { value: 'hls_variant_missing', label: 'HLS variant playlist missing' },
    { value: 'hls_chunk_count_mismatch', label: 'HLS segment count mismatch' },
    { value: 'hls_master_unplayable', label: 'HLS master playlist unplayable' },
    { value: 'hls_unreadable', label: 'HLS files unreadable' },
    { value: 'mp3val_error', label: 'mp3val error' },
    { value: 'mp3val_warning', label: 'mp3val warning' },
    { value: 'mp3val_not_run', label: 'mp3val not run' },
    { value: 'normalization_metadata_missing', label: 'Normalization metadata missing' },
];

/** Mirror of `App\Enums\OriginalBitrateStatusValue`. */
export const ORIGINAL_BITRATE_STATUS_OPTIONS: AudioFileIssueEnumOption<NonNullable<OriginalBitrateInspectionStatus>>[] = [
    { value: 'valid', label: 'Valid' },
    { value: 'invalid', label: 'Invalid' },
    { value: 'missing', label: 'Missing' },
];
